import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Send,
  User,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Building2,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";

const ContactForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Full name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string()
      .matches(/^[0-9+\-\s()]+$/, "Invalid phone number format")
      .min(10, "Phone number must be at least 10 digits")
      .required("Phone number is required"),
    company: Yup.string().min(2, "Company name must be at least 2 characters"),
    subject: Yup.string().required("Subject is required"),
    message: Yup.string()
      .min(10, "Message must be at least 10 characters")
      .required("Message is required"),
  });

  const subjectOptions = [
    { value: "", label: "Select a subject" },
    { value: "general-inquiry", label: "General Inquiry" },
    { value: "technical-support", label: "Technical Support" },
    { value: "billing-question", label: "Billing Question" },
    { value: "feature-request", label: "Feature Request" },
    { value: "partnership", label: "Partnership Opportunity" },
    { value: "demo-request", label: "Request a Demo" },
    { value: "other", label: "Other" },
  ];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitStatus("loading");

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Here you would typically send the data to your backend
      //console.log("Contact form submitted:", values);

      setSubmitStatus("success");
      setIsSubmitted(true);
      resetForm();
      toast.success("Thank you! Your message has been sent successfully.");

      // Reset success state after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      setSubmitStatus("error");
      toast.error(
        "Sorry, there was an error sending your message. Please try again."
      );
      setTimeout(() => setSubmitStatus(null), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="contact-form"
      className="py-20 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-12 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-red-100 to-blue-100 rounded-full border border-red-200/50">
              <MessageSquare className="mr-2 w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                Send us a Message
              </span>
            </div>
            <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
              Get in Touch
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Fill out the form below and we'll get back to you as soon as
              possible. We're here to help with any questions about our
              platform.
            </p>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            variants={itemVariants}
            className="p-8 bg-white rounded-2xl border border-gray-100 shadow-xl md:p-12"
          >
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="py-12 text-center"
                >
                  <div className="flex justify-center items-center mx-auto mb-6 w-20 h-20 bg-red-100 rounded-full">
                    <CheckCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-gray-800">
                    Message Sent!
                  </h3>
                  <p className="mb-6 text-gray-600">
                    Thank you for reaching out. We've received your message and
                    will get back to you within 24 hours.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setIsSubmitted(false);
                      setSubmitStatus(null);
                    }}
                    className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-red-600 to-blue-600 rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    Send Another Message
                  </motion.button>
                </motion.div>
              ) : (
                <Formik
                  initialValues={{
                    fullName: "",
                    email: "",
                    phone: "",
                    company: "",
                    subject: "",
                    message: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting, values }) => (
                    <Form className="space-y-6">
                      {/* Name and Email Row */}
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label
                            htmlFor="fullName"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Full Name *
                          </label>
                          <div className="relative">
                            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                              <User className="w-5 h-5 text-gray-400" />
                            </div>
                            <Field
                              type="text"
                              name="fullName"
                              placeholder="John Doe"
                              className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>
                          <ErrorMessage
                            name="fullName"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="email"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Email Address *
                          </label>
                          <div className="relative">
                            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                              <Mail className="w-5 h-5 text-gray-400" />
                            </div>
                            <Field
                              type="email"
                              name="email"
                              placeholder="john@example.com"
                              className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>
                      </div>

                      {/* Phone and Company Row */}
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label
                            htmlFor="phone"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Phone Number *
                          </label>
                          <div className="relative">
                            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                              <Phone className="w-5 h-5 text-gray-400" />
                            </div>
                            <Field
                              type="tel"
                              name="phone"
                              placeholder="+234 801 234 5678"
                              className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="company"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Company Name
                          </label>
                          <div className="relative">
                            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                              <Building2 className="w-5 h-5 text-gray-400" />
                            </div>
                            <Field
                              type="text"
                              name="company"
                              placeholder="Your Company"
                              className="py-3 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                          </div>
                          <ErrorMessage
                            name="company"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>
                      </div>

                      {/* Subject */}
                      <div>
                        <label
                          htmlFor="subject"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Subject *
                        </label>
                        <Field
                          as="select"
                          name="subject"
                          className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                          {subjectOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="subject"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      {/* Message */}
                      <div>
                        <label
                          htmlFor="message"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Message *
                        </label>
                        <Field
                          as="textarea"
                          name="message"
                          rows={6}
                          placeholder="Tell us how we can help you..."
                          className="px-4 py-3 w-full rounded-lg border border-gray-300 transition-all duration-300 resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                        <ErrorMessage
                          name="message"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                        <div className="mt-2 text-sm text-right text-gray-500">
                          {values.message?.length || 0}/500 characters
                        </div>
                      </div>

                      {/* Submit Button */}
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                        className="flex justify-center items-center px-8 py-4 w-full font-semibold text-white bg-gradient-to-r from-red-600 to-blue-600 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                            Sending Message...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 w-5 h-5" />
                            Send Message
                          </>
                        )}
                      </motion.button>

                      {/* Status Messages */}
                      <AnimatePresence>
                        {submitStatus === "error" && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex items-center p-4 bg-red-50 rounded-lg border border-red-200"
                          >
                            <AlertCircle className="mr-2 w-5 h-5 text-red-500" />
                            <span className="text-red-700">
                              There was an error sending your message. Please
                              try again.
                            </span>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Form>
                  )}
                </Formik>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
